import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { WhiteLabelService } from "src/app/core/util/white-label.service";
import { COUNTRY_BASE_URL } from "src/app/main/admin/referentials/country/shared/country.service";
import { CURRENCY_BASE_URL } from "src/app/main/admin/referentials/currency/shared/currency.service";
import { VAT_RATE_BASE_URL } from "src/app/main/admin/referentials/vat-rate/shared/vat-rate.service";
import { LayoutService } from "src/app/main/layout/service/app.layout.service";
import { environment } from "src/environments/environment";
import { AuthService } from "../../core/auth/auth.service";
import { UserInfo } from "../login/shared/user-info.model";
import { Wcsw } from "../login/shared/wcsw.model";
import { WcswState } from "../login/shared/wcsw-state.model";

// This service is used to do all http calls that manipulate user account data, e.g. login user, refresh user data, etc.
@Injectable({
  providedIn: "root",
})
export class UserService {
  // Session management
  // Those value help with auto *instant* logout feature
  // If this *instant* logout feature fails, the user will simply be logout by force at an unknown time
  // that is always shorter than token validity which is 6 mins
  // sessionKey: string = Math.floor(Math.random() * Date.now()).toString(36);
  sessionStart: number;

  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private oidcSecurityService = inject(OidcSecurityService);
  private whiteLabelService = inject(WhiteLabelService);
  private layoutService = inject(LayoutService);

  login(): Observable<UserInfo> {
    this.authService.clearUserState();

    return this.http
      .get<UserInfo>(`${environment.baseUrl}/engine/user/login`)
      .pipe(
        map((userInfo) => {
          this.sessionStart = Date.now();

          // Initialize layout configuration from user preferences
          this.layoutService.initConfigFromUserInfo(userInfo.layout);

          const userState = {
            userInfo,
            wcswState: this.makeDefaultWcswState(userInfo.wcsw),
          };
          this.authService.saveUserState(userState);

          setTimeout(() => this.autoLogout(), 5000);
          return userInfo;
        }),
      );
  }

  private makeDefaultWcswState(wcsw: Wcsw): WcswState {
    if (wcsw) {
      return {
        selectedPersons: wcsw.personOptions,
        selectedOrganizations: wcsw.organizationOptions,
        selectedBranches: wcsw.branchOptions,
        selectedDepartments: wcsw.departmentOptions,
        selectedGroups: wcsw.groupOptions,
        selectionIsPristine: true,
        flattenedPersons: wcsw.flattenedPersons,
        onlyMe: false,
      };
    }
    return {
      selectedPersons: [],
      selectedOrganizations: [],
      selectedBranches: [],
      selectedDepartments: [],
      selectedGroups: [],
      selectionIsPristine: true,
      flattenedPersons: [],
      onlyMe: false,
    };
  }

  autoLogout(): void {
    const lastLogoutDate = localStorage.getItem("autoLogout");
    if (lastLogoutDate && this.sessionStart < Number(lastLogoutDate)) {
      this.logout();
    }

    setTimeout(() => {
      this.autoLogout();
    }, 5000);
  }

  logout(): void {
    localStorage.setItem("autoLogout", `${Date.now()}`);
    this.authService.clearUserState();

    this.oidcSecurityService.logoff().subscribe({
      error: () => {
        this.oidcSecurityService.logoffLocal();
        document.location.href = `${this.whiteLabelService.appUrl}/`;
      },
      complete: () => {
        this.oidcSecurityService.logoffLocal();
      },
    });

    // This is not really required but we need a way to refresh cached values
    // We simply chose to clear this cache after a logout action
    this.clearLocalStorageReferentialsValues();
  }

  forceLogout(): void {
    localStorage.setItem("autoLogout", `${Date.now()}`);
    this.authService.clearUserState();
    this.oidcSecurityService.logoffLocal();

    // This is not really required but we need a way to refresh cached values
    // We simply chose to clear this cache after a logout action
    this.clearLocalStorageReferentialsValues();
  }

  updateProfile(userProfile: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    language: string;
  }): Observable<UserInfo> {
    return this.http.put<UserInfo>(
      `${environment.baseUrl}/engine/user/profile`,
      userProfile,
    );
  }

  clearLocalStorageReferentialsValues(): void {
    localStorage.removeItem(CURRENCY_BASE_URL);
    localStorage.removeItem(COUNTRY_BASE_URL);
    localStorage.removeItem(VAT_RATE_BASE_URL);
  }
}
